// Vendor Program v2 PR 1 — loan-level borrower-improvement goals + AI
// distillation. Goals anchor on the LOAN; vendor assignment is an
// independent, freely mutable property (E1-VM judgment). Reads gated
// Vendors.Read, writes Vendors.Manage.

import { api } from "@/api/client";

export type LoanGoalStatus =
  | "proposed"
  | "active"
  | "achieved"
  | "retired"
  /** E1-VM rejected the proposed goal (recoverable via un-discard; never
   *  reaches the borrower or any vendor surface). */
  | "discarded";
export type LoanGoalSource = "ai" | "manual";
export type LoanGoalPriority = "high" | "standard" | "low";

export interface LoanGoalCitationDto {
  sourceType: "document" | "generated-report" | string;
  reference: string;
  excerpt: string | null;
}

export interface LoanGoalDto {
  id: string;
  loanId: string;
  title: string;
  description: string;
  status: LoanGoalStatus;
  source: LoanGoalSource;
  suggestedVendorType: string | null;
  assignedPartyAssignmentId: string | null;
  assignedVendorId: string | null;
  assignedVendorName: string | null;
  citations: LoanGoalCitationDto[];
  createdAt: string;
  approvedAt: string | null;
  closedAt: string | null;
  /** The objective's verbatim "done when" criteria from the source report. */
  completionCriteria: string | null;
  /** Target window within the seasoning period, e.g. "1-6" (months). */
  targetMonths: string | null;
  priority: LoanGoalPriority;
  /** Model-flagged near-overlap with another proposed goal. */
  overlapNote: string | null;
}

export interface LoanGoalVendorOptionDto {
  partyAssignmentId: string;
  vendorId: string;
  vendorName: string;
  roleOnLoan: string;
}

export interface LoanGoalsOverviewDto {
  goals: LoanGoalDto[];
  vendorOptions: LoanGoalVendorOptionDto[];
}

export interface LoanGoalActivityDto {
  timestamp: string;
  actionDescription: string;
  userDisplayName: string | null;
  roleCode: string | null;
}

export interface LoanGoalDistillationResultDto {
  createdGoals: LoanGoalDto[];
  /** "folder" | "report-system" | "none" — the provenance contract. */
  sourcePath: string;
  sourceCount: number;
  sourceDescription: string;
  /** Compact resolution chain ("root found → folder 'X' → 3 documents →
   *  AI selection kept 3 → extracted 3") — the 30-second diagnosis line. */
  diagnostics: string;
}

export interface CreateLoanGoalRequest {
  title: string;
  description: string;
  suggestedVendorType?: string | null;
}

/** FIND-12: null/undefined = unchanged, "" = clear (nullable fields).
 *  Editing an ACTIVE goal requires `reason`; Proposed edits don't. */
export interface UpdateLoanGoalRequest {
  title?: string | null;
  description?: string | null;
  suggestedVendorType?: string | null;
  completionCriteria?: string | null;
  targetMonths?: string | null;
  priority?: LoanGoalPriority | null;
  reason?: string | null;
}

export const loanGoalsApi = {
  overview: (loanId: string) =>
    api.get<LoanGoalsOverviewDto>(`/portfolio-admin/loans/${loanId}/goals`),
  create: (loanId: string, body: CreateLoanGoalRequest) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loans/${loanId}/goals`, body),
  /** Synchronous AI propose run (45–90s); null vendorType = loan-wide. */
  distill: (loanId: string, vendorType: string | null) =>
    api.post<LoanGoalDistillationResultDto>(
      `/portfolio-admin/loans/${loanId}/goals/distill`,
      { vendorType },
    ),
  bulkAssign: (loanId: string, goalIds: string[], partyAssignmentId: string) =>
    api.post<LoanGoalDto[]>(`/portfolio-admin/loans/${loanId}/goals/bulk-assign`, {
      goalIds,
      partyAssignmentId,
    }),
  update: (goalId: string, body: UpdateLoanGoalRequest) =>
    api.patch<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}`, body),
  approve: (goalId: string) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/approve`),
  achieve: (goalId: string) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/achieve`),
  retire: (goalId: string) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/retire`),
  /** Proposed → Discarded (optional one-line reason; recoverable). */
  discard: (goalId: string, reason: string | null) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/discard`, { reason }),
  undiscard: (goalId: string) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/undiscard`),
  /** null partyAssignmentId = unassign. */
  assign: (goalId: string, partyAssignmentId: string | null) =>
    api.post<LoanGoalDto>(`/portfolio-admin/loan-goals/${goalId}/assign`, {
      partyAssignmentId,
    }),
  activity: (goalId: string) =>
    api.get<LoanGoalActivityDto[]>(`/portfolio-admin/loan-goals/${goalId}/activity`),
};
